import {normalizeProfile} from '@/services/api/normalizeProfile';

export function asIso(value: unknown): string {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
        const ms = value < 1e12 ? value * 1000 : value;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    const raw = String(value).trim();
    if (/^\d{10,13}$/.test(raw)) {
        const n = Number(raw);
        const ms = n < 1e12 ? n * 1000 : n;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString();
    }
    // "2026-09-22 18:00:00" / "2026-09-22T18:00:00Z"
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
    const parsed = Date.parse(normalized);
    if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
    const fallback = Date.parse(raw);
    return Number.isNaN(fallback) ? '' : new Date(fallback).toISOString();
}

function normalizePostedBy(raw: any): any {
    if (!raw) {
        return {
            id: 0,
            name: 'Unknown',
            slug: '',
            avatar: null,
            subscribed: false,
            id_verified: false,
            location: null,
        };
    }
    // Full profile envelope or userData blob
    if (raw.userData || raw.missingFields) {
        return normalizeProfile(raw);
    }
    const name = raw.name || raw.userName || raw.user_name || raw.slug || 'Unknown';
    const location = raw.location
        ? (typeof raw.location === 'string'
            ? {name: raw.location, lat: 0, lng: 0}
            : {
                name: raw.location.name
                    || [raw.location.city, raw.location.state, raw.location.country].filter(Boolean).join(', ')
                    || raw.location.address
                    || '',
                lat: Number(raw.location.lat ?? 0),
                lng: Number(raw.location.lng ?? 0),
            })
        : null;

    return {
        ...raw,
        id: Number(raw.id || 0),
        name,
        slug: raw.slug || raw.userName || String(raw.id || ''),
        avatar: raw.avatar
            || raw.profilePhoto
            || raw.profile_photo
            || raw.avatarUrl
            || raw.profilePhotoPath
            || raw.profile_photo_path
            || null,
        subscribed: Boolean(raw.isPremium ?? raw.is_premium ?? raw.subscribed),
        id_verified: Boolean(
            raw.isVerified
            ?? raw.id_verified
            ?? raw.idVerified
            ?? (String(raw.verificationStatus || '').toUpperCase() === 'APPROVED'),
        ),
        location,
        account_type: raw.account_type || raw.accountType,
    };
}

function normalizeReactions(raw: any): any[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map((r) => ({
            reaction: r.reaction || r.emojiName || r.emoji || r.name || '❤️',
            count: Number(r.count ?? r.total ?? 1),
            commentator_reacted: Boolean(r.commentator_reacted ?? r.commentatorReacted ?? r.reactedByMe ?? r.isReacted),
            id: r.id,
        }));
    }
    // Object map of emoji → count
    if (typeof raw === 'object') {
        return Object.entries(raw).map(([key, value]: [string, any]) => {
            if (value && typeof value === 'object') {
                return {
                    reaction: value.reaction || value.emojiName || key,
                    count: Number(value.count ?? value.total ?? 1),
                    commentator_reacted: Boolean(value.commentator_reacted ?? value.commentatorReacted ?? value.reactedByMe),
                    id: value.id,
                };
            }
            return {
                reaction: key,
                count: Number(value ?? 0),
                commentator_reacted: false,
            };
        }).filter((r) => r.count > 0);
    }
    return [];
}

function mediaUrl(m: any): string | null {
    if (!m) return null;
    if (typeof m === 'string') return m || null;
    const candidates = [
        m.url,
        m.signedUrl,
        m.signed_url,
        m.mediaUrl,
        m.media_url,
        m.fileUrl,
        m.file_url,
        m.src,
        m.path,
        m.photoPath,
        m.photo_path,
        m.mediaPath,
        m.media_path,
        m.filePath,
        m.file_path,
        m.thumbnailUrl,
        m.thumbnail_url,
        m.thumbnail,
        m.previewUrl,
        m.preview_url,
    ];
    for (const c of candidates) {
        if (typeof c === 'string' && c.trim()) return c.trim();
    }
    return null;
}

function normalizeMedia(raw: any): any[] {
    if (!raw) return [];
    const list = Array.isArray(raw)
        ? raw
        : (Array.isArray(raw?.items) ? raw.items
            : (Array.isArray(raw?.files) ? raw.files
                : (Array.isArray(raw?.media) ? raw.media : [])));

    return list.map((m: any, index: number) => {
        if (typeof m === 'string') {
            return m ? {id: index, url: m, type: 'IMAGE', path: m, thumbnail: m} : null;
        }
        const url = mediaUrl(m);
        if (!url) return null;
        const thumb = m.thumbnail || m.thumbnailUrl || m.thumbnail_url || m.previewUrl || m.preview_url || url;
        return {
            ...m,
            id: m.id ?? index,
            url,
            type: m.type || m.mediaType || m.media_type || 'IMAGE',
            path: m.path || url,
            thumbnail: thumb,
        };
    }).filter(Boolean);
}

function normalizeCommentReactions(rawComment: any): any[] {
    const rows = rawComment?.reactions;
    const userReaction = rawComment?.userReaction ?? rawComment?.user_reaction ?? null;
    const userReactionName = userReaction
        ? (typeof userReaction === 'string'
            ? userReaction
            : (userReaction.reaction || userReaction.emoji || userReaction.name || 'love'))
        : null;

    if (!Array.isArray(rows) || rows.length === 0) {
        const count = Number(rawComment?.reactionCount ?? rawComment?.reaction_count ?? 0);
        if (count <= 0 && !userReactionName) return [];
        return [{
            reaction: userReactionName || 'love',
            count: Math.max(count, userReactionName ? 1 : 0),
            commentator_reacted: Boolean(userReactionName),
        }].filter((r) => r.count > 0);
    }

    // App API returns one row per reactor, not aggregated counts.
    const byType = new Map<string, {reaction: string; count: number; commentator_reacted: boolean; id?: number}>();
    for (const r of rows) {
        const name = String(r.reaction || r.emojiName || r.emoji || r.name || 'love');
        const prev = byType.get(name) || {reaction: name, count: 0, commentator_reacted: false, id: r.id};
        prev.count += Number(r.count ?? 1);
        prev.commentator_reacted = prev.commentator_reacted
            || Boolean(r.commentator_reacted ?? r.commentatorReacted ?? r.reactedByMe ?? r.isReacted);
        byType.set(name, prev);
    }

    if (userReactionName) {
        const prev = byType.get(userReactionName) || {reaction: userReactionName, count: 0, commentator_reacted: false};
        prev.commentator_reacted = true;
        if (prev.count < 1) prev.count = 1;
        byType.set(userReactionName, prev);
    }

    return Array.from(byType.values());
}

/**
 * Map app-API feed comment (+ nested replies) → legacy comment shape used by FeedPostComment.vue.
 */
export function normalizeFeedComment(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw;

    const postedBy = normalizePostedBy(
        raw.posted_by
        || raw.postedBy
        || raw.commentatorUser
        || raw.commentator_user
        || raw.user
        || raw.author
        || raw.createdBy
        || raw.creator,
    );

    const content = String(
        raw.data?.content
        ?? raw.content
        ?? raw.text
        ?? raw.originalText
        ?? raw.original_text
        ?? raw.caption
        ?? raw.body
        ?? raw.message
        ?? '',
    ).trim();

    const nested = Array.isArray(raw.comments)
        ? raw.comments
        : (Array.isArray(raw.replies) ? raw.replies : []);
    const comments = nested.map((c: any) => normalizeFeedComment(c)).filter((c: any) => c && c.id);

    const reactions = normalizeCommentReactions(raw);
    const createdAt = asIso(raw.created_at || raw.createdAt);
    const updatedAt = asIso(raw.updated_at || raw.updatedAt || createdAt);

    return {
        ...raw,
        id: Number(raw.id),
        type: raw.type || 'COMMENT',
        parent_id: raw.parent_id ?? raw.parentId ?? null,
        comment_id: raw.comment_id ?? raw.commentId ?? raw.id,
        data: {
            content,
            original_content: raw.data?.original_content || raw.originalContent || raw.originalText || content,
            extra: raw.data?.extra || raw.extra || {},
        },
        posted_by: postedBy,
        comments,
        reactions,
        count: {
            reactions: Number(
                raw.reactionCount
                ?? raw.reaction_count
                ?? reactions.reduce((sum: number, r: any) => sum + Number(r.count || 0), 0),
            ),
            comments: comments.length,
        },
        media: normalizeMedia(raw.media || raw.medias || []),
        created_at: createdAt,
        updated_at: updatedAt,
        edited: Boolean(raw.edited),
    };
}

/**
 * Map app-API feed post → legacy FeedPostInterface shape used by FeedPost.vue.
 */
export function normalizeFeedPost(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw;

    // Some list payloads wrap the post: { feed: {...} } / { post: {...} }
    if (raw.feed && typeof raw.feed === 'object' && (raw.feed.id || raw.feed.content || raw.feed.media)) {
        return normalizeFeedPost({...raw, ...raw.feed});
    }
    if (raw.post && typeof raw.post === 'object' && (raw.post.id || raw.post.content || raw.post.media)) {
        return normalizeFeedPost({...raw, ...raw.post});
    }

    const postedBy = normalizePostedBy(
        raw.posted_by || raw.postedBy || raw.user || raw.author || raw.createdBy || raw.creator,
    );

    const content = String(
        raw.data?.content
        ?? raw.content
        ?? raw.text
        ?? raw.caption
        ?? raw.body
        ?? raw.message
        ?? raw.postText
        ?? raw.post_text
        ?? raw.description
        ?? '',
    ).trim();

    const media = normalizeMedia(
        raw.media
        || raw.medias
        || raw.attachments
        || raw.files
        || raw.feedMedias
        || raw.feed_medias
        || raw.photos
        || raw.images,
    );
    const reactions = normalizeReactions(raw.reactions || raw.reactionCounts || raw.emojiReactions);

    const comments = Array.isArray(raw.comments)
        ? raw.comments.map((c: any) => normalizeFeedComment(c)).filter((c: any) => c && c.id)
        : [];

    const commentsCount = Number(
        raw.count?.comments
        ?? raw.commentsCount
        ?? raw.commentCount
        ?? comments.length
        ?? 0,
    );
    const reactionsCount = Number(
        raw.count?.reactions
        ?? raw.reactionsCount
        ?? raw.reactionCount
        ?? reactions.reduce((sum: number, r: any) => sum + Number(r.count || 0), 0)
        ?? 0,
    );

    const event = raw.data?.event || raw.event
        ? normalizeEvent(raw.data?.event || raw.event)
        : undefined;

    const createdAt = asIso(raw.created_at || raw.createdAt);
    const updatedAt = asIso(raw.updated_at || raw.updatedAt || createdAt);

    const isNsfw = Boolean(raw.isNsfw ?? raw.is_nsfw ?? raw.nsfw);
    let visibility = String(raw.visibility || 'PUBLIC').toUpperCase();
    if (visibility === 'NSFW' || visibility === 'X_RATED' || visibility === 'XRATED' || isNsfw) {
        visibility = 'X_RATED';
    } else if (visibility === 'INNER_CIRCLE' || visibility === 'FRIENDS') {
        visibility = 'INNER_CIRCLE';
    } else {
        visibility = 'PUBLIC';
    }

    const mediaType = String(
        raw.media_type
        || raw.mediaType
        || (media.some((m: any) => String(m.type).toUpperCase().includes('VIDEO')) ? 'VIDEO' : 'IMAGE'),
    ).toUpperCase();

    return {
        ...raw,
        id: Number(raw.id),
        type: raw.type || 'POST',
        parent_id: raw.parent_id ?? raw.parentId ?? null,
        comment_id: raw.comment_id ?? raw.commentId ?? raw.id,
        data: {
            content,
            original_content: raw.data?.original_content || raw.originalContent || content,
            extra: raw.data?.extra || raw.extra || {},
            event,
        },
        posted_by: postedBy,
        comments,
        reactions,
        count: {
            reactions: reactionsCount,
            comments: commentsCount,
        },
        media,
        media_type: mediaType === 'VIDEO' ? 'VIDEO' : 'IMAGE',
        allow_comments: raw.allow_comments ?? raw.allowComments ?? true,
        visibility,
        created_at: createdAt,
        updated_at: updatedAt,
        edited: Boolean(raw.edited),
        approved: raw.approved !== false,
        is_nsfw: isNsfw,
    };
}

export function normalizeFeedPosts(list: any): any[] {
    if (!Array.isArray(list)) {
        if (list && typeof list === 'object') {
            if (Array.isArray(list.data)) return normalizeFeedPosts(list.data);
            if (Array.isArray(list.feeds)) return normalizeFeedPosts(list.feeds);
            if (Array.isArray(list.posts)) return normalizeFeedPosts(list.posts);
            if (Array.isArray(list.items)) return normalizeFeedPosts(list.items);
        }
        return [];
    }
    return list.map(normalizeFeedPost).filter((p) => p && p.id);
}

/**
 * Map app-API event → legacy EventInterface shape.
 */
export function normalizeEvent(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw;

    const startsAt = asIso(raw.starts_at || raw.startsAt || raw.startAt || raw.start_date);
    const endsAt = asIso(raw.ends_at || raw.endsAt || raw.endAt || raw.end_date || startsAt);

    const location = typeof raw.location === 'string'
        ? raw.location
        : (raw.location?.name
            || raw.location?.address
            || [raw.location?.city, raw.location?.state, raw.location?.country].filter(Boolean).join(', ')
            || raw.address
            || '');

    const photo = raw.photo_url
        || raw.photoUrl
        || raw.photo
        || raw.coverPhoto
        || raw.cover_photo
        || raw.image
        || raw.imageUrl
        || null;

    const organiser = normalizePostedBy(
        raw.organiser || raw.organizer || raw.host || raw.user || raw.createdBy || raw.creator,
    );

    return {
        ...raw,
        id: Number(raw.id),
        name: raw.name || raw.title || 'Event',
        description: raw.description || '',
        rules: raw.rules || '',
        photo_url: photo,
        location,
        lat: Number(raw.lat ?? raw.location?.lat ?? 0),
        lng: Number(raw.lng ?? raw.location?.lng ?? 0),
        type: raw.type,
        visibility: raw.visibility,
        capacity: Number(raw.capacity ?? 0),
        capacity_remaining: Number(raw.capacity_remaining ?? raw.capacityRemaining ?? raw.capacity ?? 0),
        public_guest_list: Boolean(raw.public_guest_list ?? raw.publicGuestList),
        allow_comments: raw.allow_comments ?? raw.allowComments ?? true,
        starts_at: startsAt,
        ends_at: endsAt,
        created_at: asIso(raw.created_at || raw.createdAt),
        updated_at: asIso(raw.updated_at || raw.updatedAt),
        organiser,
        attending: Array.isArray(raw.attending) ? raw.attending.map(normalizePostedBy) : [],
        interested: Array.isArray(raw.interested) ? raw.interested.map(normalizePostedBy) : [],
        requested: Array.isArray(raw.requested) ? raw.requested.map(normalizePostedBy) : [],
        is_interested: Boolean(raw.is_interested ?? raw.isInterested),
        is_attending: Boolean(raw.is_attending ?? raw.isAttending),
        has_requested_invite: Boolean(raw.has_requested_invite ?? raw.hasRequestedInvite),
        conversations: Number(raw.conversations ?? raw.commentsCount ?? 0),
    };
}

export function normalizeEvents(list: any): any[] {
    if (!Array.isArray(list)) return [];
    return list.map(normalizeEvent).filter((e) => e && e.id);
}
